import * as React from "react"
import { ImgS } from "ui-seeker/core"
import { IMAGE } from 'app/@common/constants'
import { formatWithFlag } from 'app/utils'
import AppSelectFilter, { selectFilterLogic, selectFilterOptions } from "../SelectFilter"

const FALLBACK_LOGO = `${IMAGE.ASSETS}/web.png`
// Apply the fallback only once: if web.png also fails, onError would re-fire forever.
const onLogoError = i => {
    if (i.target.src !== FALLBACK_LOGO) i.target.src = FALLBACK_LOGO
}

// Country code from the site name suffix ("logitravel.es" → "es"), as the Packages cells do.
const siteCountryCode = site => {
    const parts = site.split('.')
    if (parts.length < 2) return null
    const cc = parts[parts.length - 1].trim()
    return cc.toUpperCase() === 'UK' ? 'GB' : cc
}

const defaultLogoName = site => site.split('.')[0].trim()

// The -mini.png assets come in any size and aspect ratio: .filter-site-logo fixes the box (_table.scss).
// No span here: see AppSelectFilter.
const siteOptionRenderer = ({ withFlag = false, logoName = defaultLogoName } = {}) => function renderSiteOption(site) {
    return <>
        {withFlag && formatWithFlag(siteCountryCode(site) || 'globe')}
        <ImgS
            className="filter-site-logo"
            src={`${IMAGE.ASSETS}/${logoName(site)}-mini.png`}
            alt={`${site} logo`}
            onError={onLogoError}
        />
        {site}
    </>
}

// Site columns come as one site name, an array of them or a comma-separated string ("booking.com,expedia.es");
// some return the id as a number when the name is missing.
const toSiteList = val => {
    if (Array.isArray(val)) return val.filter(Boolean).map(String)
    if (typeof val === 'number') return [String(val)]
    if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean)
    return []
}

// filterOptions.logic for site columns: keep the row when it has any of the selected sites. Returns true to hide it.
export const siteFilterLogic = selectFilterLogic(toSiteList)

// Custom mui-datatables filter for site columns: AppSelectFilter with the site logo in every option.
export default function AppSiteFilter({ withFlag = false, logoName = defaultLogoName, ...props }) {
    return <AppSelectFilter {...props} toValues={toSiteList} renderOption={siteOptionRenderer({ withFlag, logoName })} />
}

// Column options for a site filter, to spread into a column's `options`:
//   options: { ...siteFilterOptions({ field: 'mainSite', label: i18n.t('report:columns.mainSite') }), ... }
// field = the column's data key, dot path allowed (site name, array or comma-separated string);
// withFlag = country flag before the logo; logoName = site → logo file name (default: text before the first dot);
// isMulti = several sites at once (rows with any of them).
export const siteFilterOptions = ({ field, label, withFlag = false, logoName, isMulti = false }) =>
    selectFilterOptions({ field, label, isMulti, toValues: toSiteList, renderOption: siteOptionRenderer({ withFlag, logoName }) })
